<template>
    <inline-descriptor ref="inlineDescriptorDisplay" v-memo="[computedModelValue, computedSchema]" :id="id"
        :model-value="computedModelValue" :schema="computedSchema"
        :token-styles="tokenStyles" :separators="separators" :is-table="isTable"
        :editable="false">
    </inline-descriptor>
</template>
<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-facing-decorator';
import { reactive } from 'vue';
import InlineDescriptor, { SchemaField, Token } from './InlineDescriptor.vue';
import { V2ParameterValueSet_ValueType, V2ParameterValueType } from '@/models/etc/parameter-types';
import { ParameterInstance, ParameterInstanceContainer } from '../../../models/interfaces/parameters/parameter-models';
import { ParameterInstanceContainerType } from '../../../models/etc/types';
import { date } from 'quasar';

@Component({
    emits: ['update:modelValue'],
    components: { InlineDescriptor, ParameterInlineDisplay }
})

/** Lightweight display ParameterInline component version */
export default class ParameterInlineDisplay extends Vue {
    @Prop({ type: String, required: true }) id!: string;
    @Prop({ type: Array, default: () => [] }) modelValue!: ParameterInstanceContainer[];
    @Prop({ type: Object, default: () => ({}) }) tokenStyles!: Record<string, string>;
    @Prop({ type: String, default: '' }) indexPrefix!: string;
    @Prop({ type: Boolean, default: false }) isTable?: boolean;

    private separators = [','];
    private parameters = reactive<ParameterInstanceContainer[]>(Array.isArray(this.modelValue) ? this.modelValue : []);

    @Watch('modelValue', { immediate: true, deep: true })
    onModelValueChange(newValue: ParameterInstanceContainer[]) {
        this.parameters.splice(0, this.parameters.length, ...(Array.isArray(newValue) ? newValue : []));
    }

    private createSchema(params: ParameterInstanceContainer[], indexPrefix: string = ''): SchemaField[] {
        const schema: SchemaField[] = [];

        params.forEach((param) => {
            const ttl = param.ListTitle;
            const insts = param.Instance;
            if (param.Type === ParameterInstanceContainerType.List && ttl) {
                // take the 1st instance as they should be the same structure in the list
                const firstInstance = ttl.Instances[0];
                const templateId = firstInstance.TemplateId;
                const nestedSchema = ttl.Instances.map((item, nestedIndex) => {
                    const newPrefix = indexPrefix + nestedIndex;
                    return this.ParameterInstanceToSchemaField(item, `param_${templateId}_${newPrefix}`, ttl.Name);
                });
                schema.push(this.ParameterInstanceToSchemaField(undefined, `param_${templateId}_${indexPrefix}arr`, '', nestedSchema));
            } else if (insts) {
                const templateId = insts.TemplateId;
                schema.push(this.ParameterInstanceToSchemaField(insts, `param_${templateId}_${indexPrefix}`, '', undefined));
            }
        });
        return schema;
    }

    get computedSchema(): SchemaField[] {
        const schema = this.createSchema(this.parameters, this.indexPrefix);
        return schema;
    }

    private getTypeFromValueType(valueType: V2ParameterValueType): string {
        switch (valueType) {
            case V2ParameterValueType.String:
                return 'string';
            case V2ParameterValueType.Int:
                return 'int';
            case V2ParameterValueType.Double:
                return 'double';
            case V2ParameterValueType.ValueSet:
                return 'select';
            case V2ParameterValueType.Record:
                return 'nested';
            default:
                return 'string';
        }
    }

    private getInputPatternFromValueType(valueType: V2ParameterValueType): RegExp | undefined {
        switch (valueType) {
            case V2ParameterValueType.String: return /^[\p{L}0-9\s]+$/u;
            case V2ParameterValueType.Int: return /^[0-9]+$/;
            case V2ParameterValueType.Double: return /[0-9.]/;
            case V2ParameterValueType.ValueSet: return /^[\p{L}0-9\s]+$/u;
            default:
                return undefined;
        }
    }

    /** TODO: StringValue заменить везде в проекте на проверку в зависимости от типа ячейки */

    createModelValue(params: ParameterInstanceContainer[], indexPrefix: string = '', modelValue: Record<string, any> = {}): Record<string, any> {
        params.forEach((param) => {
            const ttl = param.ListTitle;
            const insts = param.Instance;
            if (param.Type === ParameterInstanceContainerType.List && ttl) {
                // take the 1st instance as they should be the same structure in the list
                const firstInstance = ttl.Instances[0];
                const templateId = firstInstance.TemplateId;
                
                    const key = `param_${templateId}_${indexPrefix}arr`;
                    if (firstInstance.ValueType === V2ParameterValueType.Record && firstInstance.RecordFields) {
                        modelValue[key] = ttl.Instances.map(item => item.RecordFields || []);
                    } else {
                        const nestedModel = ttl.Instances.map(item => {
                            // use any item here as they should be always equal to the first one concerning structure
                            const value = this.ParameterInstanceToValue(item);
                            const tokens = (value.match(/(\s+|[^\s]+)/g) || []).map((tok, index) => ({
                                text: tok,
                                type: 'string', // item.ValueType === V2ParameterValueType.ValueSet ? 'valueset' : 
                                    // (item.ValueType === V2ParameterValueType.Int ? 'int' : (item.ValueType === V2ParameterValueType.Double ? 'double' : 'string')),
                                clickable: item.ValueType === V2ParameterValueType.ValueSet,
                            }));
                            const fullTokens: Token[] = [];
                            tokens.forEach((token, index) => {
                                if (/\s+/.test(token.text)) {
                                    fullTokens.push({ text: ' ', type: 'space', clickable: false });
                                } else {
                                    fullTokens.push(token);
                                }
                            });
                            return fullTokens;
                        });
                        modelValue[key] = nestedModel;
                    }
                
            } else if (insts && insts.ValueType === V2ParameterValueType.Record && insts.RecordFields) {
                const templateId = insts.TemplateId;
                const key = `param_${templateId}_${indexPrefix}`;
                modelValue[key] = insts.RecordFields;
            } else if (insts) {
                // console.log('build:')
                // console.log(param)
                const value = this.ParameterInstanceToValue(insts); // insts.ValueType == 4 && insts.SelectedValue?.Id ? 
                            // insts.SelectedValue.Name : insts.StringValue || '';

                // console.log(value)
                const tokens = (value.match(/(\s+|[^\s]+)/g) || []).map((tok: string, index: number) => ({
                    text: tok,
                    type: 'string', // insts.ValueType === V2ParameterValueType.ValueSet ? 'valueset' : 
                        // (insts.ValueType === V2ParameterValueType.Int ? 'int' : (insts.ValueType === V2ParameterValueType.Double ? 'double' : 'string')),
                    clickable: insts.ValueType === V2ParameterValueType.ValueSet,
                }));
                const fullTokens: Token[] = [];
                tokens.forEach((token: Token, index: number) => {
                    if (/\s+/.test(token.text)) {
                        fullTokens.push({ text: ' ', type: 'space', clickable: false });
                    } else {
                        fullTokens.push(token);
                    }
                });
                const templateId = insts.TemplateId;
                const key = `param_${templateId}_${indexPrefix}`;
                modelValue[key] = fullTokens;

                // console.log(modelValue[key])
            }
        });

        // console.log('computed: ')
        // console.log(JSON.stringify(modelValue))
        return modelValue;
    }

    get computedModelValue(): Record<string, any> {
        return this.createModelValue(this.parameters, this.indexPrefix);
    }

    private ParameterInstanceToSchemaField(item: ParameterInstance | undefined, key: string, title: string, 
    nestedSchema: SchemaField[] | undefined = undefined): SchemaField {
        return {
            key,
            title,
            type: item ? this.getTypeFromValueType(item.ValueType) : '',
            inputPattern: item ? this.getInputPatternFromValueType(item.ValueType) : undefined,
            options: item &&
                (item.ValueType === V2ParameterValueType.ValueSet ||
                item.TemplateId === 999)
                ? [
                    {
                        ValueSet: item.ValueSet,
                        TemplateId: item.TemplateId,
                        selectedId: item.SelectedValue?.Id,
                    },
                    ]
                : undefined,
            separator: item ? this.separators[0] : undefined,
            nestedSchema,
            allowDelete: false,
            prefix: undefined,
            isList: nestedSchema && nestedSchema.length ? true : false,
            placeholder: item ? item.Name : '',
            component: item && item.ValueType === V2ParameterValueType.Record ? this.$options.components?.ParameterInlineDisplay : undefined
        }
    }

    private ParameterInstanceToValue(param: ParameterInstance): string {
        switch (param.ValueType) {
            case V2ParameterValueType.String:
                return (param.StringValue || '')
            case V2ParameterValueType.Int:
            case V2ParameterValueType.Double:
                return ''+param.NumberValue || '';
            case V2ParameterValueType.Formula:
                return param.NumberValue ? ''+param.NumberValue : '';
            case V2ParameterValueType.Bool:
                return param.BoolValue != null ? (param.BoolValue ? 'true' : 'false') : 'null';
            case V2ParameterValueType.Date:
                return param.DateValue ? date.formatDate(new Date(""+param.DateValue), 'DD.MM.YY') || '' : ''
            case V2ParameterValueType.ValueSet:
                switch (param.ValueSet?.ValueType) {
                    case V2ParameterValueSet_ValueType.String:
                    // falls through
                    case V2ParameterValueSet_ValueType.Int:
                    case V2ParameterValueSet_ValueType.Decimal:
                        return (param.SelectedValue?.Name || '');
                    case V2ParameterValueSet_ValueType.Date:
                        return param.SelectedValue?.Name ? date.formatDate(new Date(""+param.SelectedValue?.Name), 'DD.MM.YY') || '' : '';
                    default:
                        return '';
                }
            default:
                return '';
        }
    }
}
</script>
<style scoped>
ul {
    list-style: none;
    padding: 0;
    margin: 0;
    min-width: 150px;
    max-width: fit-content;
}

li {
    padding: 8px;
    cursor: pointer;
}

li:hover {
    background: #f0f0f0;
}
</style>